import { Badge } from '@/components/ui';
import type { JourneyStep, JourneyStepStatus, PackageItem } from '@/types/campaign';
import styles from './JourneyTimeline.module.css';

type BadgeVariant = 'neutral' | 'info' | 'success' | 'warning';

const STEP_STATUS_LABELS: Record<JourneyStepStatus, string> = {
  PENDING: 'Pendente',
  SCHEDULED: 'Agendado',
  COMPLETED: 'Concluído',
  SKIPPED: 'Pulado',
};

const STEP_STATUS_BADGE_VARIANT: Record<JourneyStepStatus, BadgeVariant> = {
  PENDING: 'neutral',
  SCHEDULED: 'info',
  COMPLETED: 'success',
  SKIPPED: 'warning',
};

const DOT_STYLE_MAP: Record<JourneyStepStatus, string> = {
  PENDING: styles.stepDotPending,
  SCHEDULED: styles.stepDotScheduled,
  COMPLETED: styles.stepDotCompleted,
  SKIPPED: styles.stepDotSkipped,
};

const LINE_STYLE_MAP: Record<JourneyStepStatus, string> = {
  PENDING: styles.stepLinePending,
  SCHEDULED: styles.stepLineScheduled,
  COMPLETED: styles.stepLineCompleted,
  SKIPPED: styles.stepLineSkipped,
};

/**
 * Represents a step in the timeline. Supports both JourneyStep
 * (for enrolled patients) and PackageItem (for package preview).
 */
interface TimelineStepData {
  id: string;
  procedureTypeName: string;
  status: JourneyStepStatus;
  orderIndex: number;
  scheduledAt?: string | null;
  completedAt?: string | null;
  notes?: string | null;
  quantity?: number;
}

interface JourneyTimelineProps {
  /** Journey steps from an enrolled patient journey. */
  steps?: JourneyStep[];
  /** Package items for preview mode (no journey yet). */
  packageItems?: PackageItem[];
  /** Label for the aria-label on the timeline. */
  ariaLabel?: string;
}

/**
 * Visual vertical timeline showing journey steps or package items.
 *
 * Each step shows:
 * - procedure name with order index
 * - status badge (pending=gray, scheduled=blue, completed=green, skipped=yellow)
 * - connecting lines between steps
 * - scheduled/completed dates when available
 *
 * Used both on admin campaign detail and (later) patient portal.
 * Fully accessible with semantic HTML and ARIA attributes.
 */
export function JourneyTimeline({
  steps,
  packageItems,
  ariaLabel = 'Linha do tempo da jornada',
}: JourneyTimelineProps) {
  const timelineSteps: TimelineStepData[] = buildTimelineSteps(steps, packageItems);

  if (timelineSteps.length === 0) {
    return (
      <div className={styles.emptyState} role="status">
        <p>Nenhuma etapa cadastrada.</p>
      </div>
    );
  }

  const sorted = [...timelineSteps].sort((a, b) => a.orderIndex - b.orderIndex);

  return (
    <ol className={styles.timeline} aria-label={ariaLabel} role="list">
      {sorted.map((step, index) => {
        const isLast = index === sorted.length - 1;
        const dotClass = DOT_STYLE_MAP[step.status];
        const lineClass = LINE_STYLE_MAP[step.status];

        return (
          <li key={step.id} className={styles.step}>
            <div className={styles.stepIndicator} aria-hidden="true">
              <span className={[styles.stepDot, dotClass].join(' ')} />
              {!isLast && (
                <span className={[styles.stepLine, lineClass].join(' ')} />
              )}
            </div>
            <div className={styles.stepContent}>
              <div className={styles.stepHeader}>
                <span className={styles.stepOrder}>
                  {step.orderIndex + 1}.
                </span>
                <span className={styles.stepName}>
                  {step.procedureTypeName}
                  {step.quantity !== undefined && step.quantity > 1 && (
                    <> ({step.quantity}x)</>
                  )}
                </span>
                <Badge variant={STEP_STATUS_BADGE_VARIANT[step.status]}>
                  {STEP_STATUS_LABELS[step.status]}
                </Badge>
              </div>
              <div className={styles.stepDetails}>
                {step.scheduledAt && (
                  <span className={styles.stepDate}>
                    Agendado: {formatDateTime(step.scheduledAt)}
                  </span>
                )}
                {step.completedAt && (
                  <span className={styles.stepDate}>
                    Concluído: {formatDateTime(step.completedAt)}
                  </span>
                )}
                {step.notes && (
                  <span className={styles.stepNotes}>{step.notes}</span>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function buildTimelineSteps(
  steps?: JourneyStep[],
  packageItems?: PackageItem[],
): TimelineStepData[] {
  if (steps && steps.length > 0) {
    return steps.map((step) => ({
      id: step.id,
      procedureTypeName: step.procedureTypeName,
      status: step.status,
      orderIndex: step.orderIndex,
      scheduledAt: step.scheduledAt,
      completedAt: step.completedAt,
    }));
  }

  if (packageItems && packageItems.length > 0) {
    return packageItems.map((item) => ({
      id: item.id,
      procedureTypeName: item.procedureTypeName,
      status: 'PENDING' as const,
      orderIndex: item.orderIndex,
      notes: item.notes,
      quantity: item.quantity,
    }));
  }

  return [];
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
